import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';

import { JwtHelperService } from '@auth0/angular-jwt';
import { LoginResult } from '@models/login-info';
import { UserInfo } from '@models/user';
import { tap } from 'rxjs';

import { LOCAL_STORAGE_REFRESH_TOKEN, LOCAL_STORAGE_TOKEN } from './auth.const';

const AUTH_API = '/api/auth';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private _http = inject(HttpClient);

  // #region Token
  private _token = signal<string | null>(localStorage.getItem(LOCAL_STORAGE_TOKEN));

  getToken(): string | null {
    return this._token();
  }

  private _setToken(token: string | null) {
    if (token) {
      localStorage.setItem(LOCAL_STORAGE_TOKEN, token);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_TOKEN);
    }
    this._token.set(token);
  }

  private _clearToken() {
    this._setToken(null);
  }
  // #endregion Token

  // #region RefreshToken
  private _refreshToken = signal<string | null>(localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN));

  getRefreshToken(): string | null {
    return this._refreshToken();
  }

  private _setRefreshToken(refreshToken: string | null) {
    if (refreshToken) {
      localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN, refreshToken);
    } else {
      localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN);
    }
    this._refreshToken.set(refreshToken);
  }

  private _clearRefreshToken() {
    this._setRefreshToken(null);
  }
  // #endregion RefreshToken

  readonly user = computed(() => {
    const t = this._token();
    return t ? new JwtHelperService().decodeToken<UserInfo>(t) : null;
  });

  readonly loggedIn = computed(() => !!this.user());

  login(email: string, password: string) {
    return this._http
      .post<LoginResult>(`${AUTH_API}/login`, { email, password })
      .pipe(tap((res) => this._handleTokenResponse(res)));
  }

  logout() {
    this._clearToken();
    this._clearRefreshToken();
  }

  registerUser(email: string, password: string) {
    return this._http
      .post<LoginResult>(`${AUTH_API}/register`, { email, password })
      .pipe(tap((res) => this._handleTokenResponse(res)));
  }

  updateUser(updatedInfo: UserInfo) {
    return this._http
      .put<LoginResult>(`/api/user/self`, updatedInfo)
      .pipe(tap((res) => this._handleTokenResponse(res)));
  }

  refreshToken(token: string) {
    return this._http
      .post<LoginResult>(`${AUTH_API}/refreshtoken`, {
        refreshToken: token,
      })
      .pipe(tap((response) => this._handleTokenResponse(response)));
  }

  private _handleTokenResponse(result: LoginResult) {
    this._setToken(result.token);
    this._setRefreshToken(result.refreshToken);
  }
}
